import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Loader2, Star, MapPin, Check, X, Rocket, Shield, Flame, Award, User, Phone, Mail, Info, Sparkles, RotateCcw, Plus, AlertTriangle } from 'lucide-react';
import { ReliabilityBadge, CommitmentBadge } from './ReliabilityBadge';
import { formatDateMMDDYYYY } from '../utils/timeFormat';
import { toast } from 'sonner';

/**
 * WhyThisTalent Tooltip - Shows reasons why a talent is recommended
 */
const WhyThisTalentTooltip = ({ reasons = [], children }) => {
  if (!reasons || reasons.length === 0) return children;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-slate-900 text-white p-3">
          <p className="text-xs font-semibold mb-2 text-cyan-400">Why this talent?</p>
          <div className="space-y-1">
            {reasons.slice(0, 3).map((reason, idx) => (
              <p key={idx} className="text-xs text-gray-200">• {reason}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * QuickFillModal - Shows auto-matched talent for a shift
 */
const QuickFillModal = ({ 
  isOpen, 
  onClose, 
  shiftId, 
  shiftDetails = {},
  onTalentSelected 
}) => {
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [shiftInfo, setShiftInfo] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(new Set());
  const [booking, setBooking] = useState(false);
  
  // Smart Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && shiftId) {
      fetchQuickFillMatches();
      fetchSmartSuggestions();
    }
  }, [isOpen, shiftId]);

  const fetchSmartSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await api.get(`/suggestions/recommended-for-shift/${shiftId}`);
      setSuggestions(res.data.recommendations || []);
    } catch (error) {
      console.error('Smart Suggestions error:', error);
      // Silent fail - suggestions are bonus
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const fetchQuickFillMatches = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/calendar/bookings/${shiftId}/quick-fill`, {
        max_results: 15
      });
      setMatches(res.data.matches || []);
      setShiftInfo(res.data.shift_details || {});
    } catch (error) {
      console.error('Quick Fill error:', error);
      toast.error('Failed to load matching talent');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (userId) => {
    setSelectedTalent(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleBook = async () => {
    if (selectedTalent.size === 0) {
      toast.error('Select at least one talent to book');
      return;
    }

    setBooking(true);
    try {
      const talentIds = Array.from(selectedTalent);
      // Book each selected talent
      for (const talentId of talentIds) {
        await api.post(`/admin/calendar/bookings/${shiftId}/assign`, {
          talent_id: talentId
        });
      }
      toast.success(`Booked ${talentIds.length} talent for this shift`);
      onTalentSelected?.(talentIds);
      onClose();
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book talent');
    } finally {
      setBooking(false);
    }
  };

  const handleRebook = async (talentId, talentName) => {
    try {
      await api.post(`/suggestions/rebook/${talentId}?shift_id=${shiftId}`);
      toast.success(`Rebooked ${talentName} for this shift!`);
      onTalentSelected?.([talentId]);
      // Refresh lists
      fetchQuickFillMatches();
      fetchSmartSuggestions();
    } catch (error) {
      console.error('Rebook error:', error);
      toast.error(error.response?.data?.detail || 'Failed to rebook talent');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quick-fill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Rocket className="w-5 h-5 text-purple-500" />
            Quick Fill - Smart Talent Matching
          </DialogTitle>
          <DialogDescription>
            Auto-matched talent ranked by certifications, reliability, commitment, and proximity.
          </DialogDescription>
        </DialogHeader>

        {/* Shift Info */}
        {shiftInfo && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{shiftInfo.venue_name}</p>
                <p className="text-sm text-gray-500">
                  {shiftInfo.role_name} • {shiftInfo.city}, {shiftInfo.state}
                </p>
                <p className="text-xs text-gray-400">
                  {formatDateMMDDYYYY(shiftInfo.shift_date)} {shiftInfo.is_multiday && '(Multi-day)'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-orange-600 font-medium">
                  {shiftInfo.spots_remaining} spots remaining
                </p>
                {shiftInfo.certification_required && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 mt-1">
                    <Award className="w-3 h-3 mr-1" />
                    Cert Required
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            <span className="ml-3 text-gray-500">Finding best matches...</span>
          </div>
        )}

        {/* Smart Suggestions Section - Previously Booked & Recommended */}
        {!loading && suggestions.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold text-gray-900">Smart Suggestions</h3>
            </div>
            
            <div className="space-y-2">
              {suggestions.slice(0, 5).map((talent, idx) => (
                <Card 
                  key={talent.talent_id}
                  className={`transition-all ${
                    talent.suggestion_type === 'previously_booked' 
                      ? 'border-amber-200 bg-amber-50/50'
                      : 'border-cyan-200 bg-cyan-50/50'
                  }`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={talent.profile_photo_url} />
                        <AvatarFallback className="bg-amber-100 text-amber-700">
                          {talent.first_name?.[0]}{talent.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 text-sm">{talent.display_name}</p>
                          <Badge className={`text-[10px] ${
                            talent.suggestion_type === 'previously_booked'
                              ? 'bg-amber-500 text-white'
                              : 'bg-cyan-500 text-white'
                          }`}>
                            {talent.suggestion_label}
                          </Badge>
                        </div>
                        
                        {/* Why This Talent */}
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                          {talent.why_this_talent?.slice(0, 2).map((reason, i) => (
                            <span key={i}>{reason}</span>
                          ))}
                        </div>
                        
                        {/* Badges */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {/* Paid Visibility Boost — yellow "Boosted" chip so
                              customers who paid for boost see visible value and
                              clients can tell who's boosted at a glance. */}
                          {talent.is_boosted && (
                            <Badge
                              className="text-[10px] bg-amber-400 hover:bg-amber-400 text-amber-900 border-0 shadow-sm font-semibold"
                              title={talent.featured_until ? `Boosted until ${new Date(talent.featured_until).toLocaleString()}` : 'Paid visibility boost active'}
                              data-testid={`tag-boosted-${talent.talent_id}`}
                            >
                              ★ Boosted
                            </Badge>
                          )}
                          {/* Returning-talent primary tag — platform or legacy history */}
                          {talent.worked_with_client_before && (
                            <Badge
                              className="text-[10px] bg-emerald-600 hover:bg-emerald-600 text-white border-0"
                              title={
                                talent.history_source === 'legacy'
                                  ? `Legacy: ${talent.client_legacy_events || 0} past events`
                                  : talent.history_source === 'both'
                                    ? `${talent.client_booking_count_platform || 0} platform + ${talent.client_legacy_events || 0} legacy events`
                                    : `${talent.client_booking_count_platform || 0} previous bookings`
                              }
                              data-testid={`tag-worked-before-${talent.talent_id}`}
                            >
                              Worked with you before
                            </Badge>
                          )}
                          {/* IDv override flag — legacy talent whose platform
                              IDv is pending. Shown next to the returning tag so
                              clients know to pair them with a checked-in lead. */}
                          {talent.verification_pending && (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-amber-50 border-amber-300 text-amber-800"
                              title={`Platform ID verification is ${talent.idv_status || 'pending'}. Legacy relationship override applied.`}
                              data-testid={`tag-verification-pending-${talent.talent_id}`}
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Verification pending
                            </Badge>
                          )}
                          {/* Secondary 'Experienced' tag from lifetime legacy_jobs_worked */}
                          {talent.experienced_legacy && !talent.worked_with_client_before && (
                            <Badge
                              className="text-[10px] bg-slate-700 hover:bg-slate-700 text-white border-0"
                              title={`${talent.legacy_jobs_worked} lifetime pre-platform events`}
                              data-testid={`tag-experienced-${talent.talent_id}`}
                            >
                              Experienced
                            </Badge>
                          )}
                          {talent.reliability_badge === 'top_talent' && (
                            <ReliabilityBadge badge="top_talent" size="xs" showScore={false} />
                          )}
                          {talent.reliability_badge === 'reliable' && (
                            <ReliabilityBadge badge="reliable" size="xs" showScore={false} />
                          )}
                          {talent.commitment_badge === 'high_commitment' && (
                            <CommitmentBadge badge="high_commitment" size="xs" />
                          )}
                        </div>
                      </div>

                      {/* Rebook Button */}
                      {talent.can_rebook && (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRebook(talent.talent_id, talent.display_name);
                          }}
                          data-testid={`rebook-btn-${idx}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Rebook
                        </Button>
                      )}
                      
                      {!talent.can_rebook && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelect(talent.talent_id);
                          }}
                        >
                          {selectedTalent.has(talent.talent_id) ? (
                            <>
                              <Check className="w-3 h-3 mr-1 text-green-500" />
                              Selected
                            </>
                          ) : (
                            <>
                              <Plus className="w-3 h-3 mr-1" />
                              Select
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="border-t border-gray-200 my-4" />
          </div>
        )}

        {/* Results */}
        {!loading && matches.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 font-medium">
              Found {matches.length} matching talent
            </p>
            
            {matches.map((talent, idx) => (
              <WhyThisTalentTooltip key={talent.user_id} reasons={talent.match_reasons}>
                <Card 
                  className={`cursor-pointer transition-all ${
                    selectedTalent.has(talent.user_id) 
                      ? 'ring-2 ring-purple-500 bg-purple-50' 
                      : 'hover:border-purple-200'
                  }`}
                  onClick={() => toggleSelect(talent.user_id)}
                  data-testid={`quick-fill-talent-${idx}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {/* Selection indicator */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                        selectedTalent.has(talent.user_id)
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : 'border-gray-300'
                      }`}>
                        {selectedTalent.has(talent.user_id) && <Check className="w-4 h-4" />}
                      </div>

                      {/* Avatar */}
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={talent.profile_photo_url} />
                        <AvatarFallback className="bg-cyan-100 text-cyan-700">
                          {talent.first_name?.[0]}{talent.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">{talent.display_name}</p>
                          {/* Match score rank */}
                          {idx < 3 && (
                            <Badge className={`text-[10px] ${
                              idx === 0 ? 'bg-amber-500 text-white' :
                              idx === 1 ? 'bg-gray-400 text-white' :
                              'bg-amber-700 text-white'
                            }`}>
                              #{idx + 1}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {talent.city}, {talent.state}
                          </span>
                          {talent.avg_rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              {talent.avg_rating}
                            </span>
                          )}
                          <span>{talent.completed_gigs} gigs</span>
                        </div>
                        
                        {/* Reliability Badges - Max 2 */}
                        <div className="flex gap-1 mt-1.5">
                          {talent.reliability_badge === 'top_talent' && (
                            <ReliabilityBadge badge="top_talent" size="xs" showScore={false} />
                          )}
                          {talent.reliability_badge === 'reliable' && (
                            <ReliabilityBadge badge="reliable" size="xs" showScore={false} />
                          )}
                          {talent.reliability_badge === 'new_talent' && (
                            <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500">
                              <User className="w-3 h-3 mr-1" />New
                            </Badge>
                          )}
                          {talent.commitment_badge === 'high_commitment' && (
                            <CommitmentBadge badge="high_commitment" size="xs" />
                          )}
                        </div>
                      </div>

                      {/* Why This Talent indicator */}
                      <div className="flex items-center gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-gray-400 hover:text-purple-500 cursor-help">
                                <Info className="w-4 h-4" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="font-semibold text-xs mb-1">Why this talent?</p>
                              {talent.match_reasons?.map((reason, i) => (
                                <p key={i} className="text-xs">• {reason}</p>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        {/* Contact icons */}
                        {talent.phone && (
                          <a 
                            href={`tel:${talent.phone}`} 
                            className="text-gray-400 hover:text-cyan-500"
                            onClick={e => e.stopPropagation()}
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                        {talent.email && (
                          <a 
                            href={`mailto:${talent.email}`} 
                            className="text-gray-400 hover:text-cyan-500"
                            onClick={e => e.stopPropagation()}
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </WhyThisTalentTooltip>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && matches.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No matching talent found for this shift.</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting requirements or check back later.</p>
          </div>
        )}

        {/* Actions */}
        {!loading && matches.length > 0 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-gray-500">
              {selectedTalent.size} selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleBook}
                disabled={selectedTalent.size === 0 || booking}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="quick-fill-book-btn"
              >
                {booking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Book Selected ({selectedTalent.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickFillModal;
export { WhyThisTalentTooltip };
